import { TFunction } from 'i18next';
import {
  SearchItemType,
  SearchItemTypeEnum,
  SearchPeopleItem,
  SectionEnum,
} from 'types/nausearchType';

export const isSearchPeopleItem = (item: SearchItemType): item is SearchPeopleItem =>
  item.typeId === SearchItemTypeEnum.people;

const mapOnce = {
  D: 'date',
  F: 'fileName',
  PLACE: 'cityLowerCase',
  S: 'titleLowerCase',
  T: 'fileMimeType',
  XCPS: 'rubric',
  XCPSTEXT: 'rubric',
  XCPT: 'type',
  XCPTTEXT: 'type',
  XF: 'fileNameUpperCase',
  XFID: 'id',
  XFSIZE: 'fileSize',
  XPLACE: 'city',
  XS: 'title',
  XUREF: 'ref',
};

const mapMultiple = {
  A: 'author',
  K: 'keyWords',
  XA: 'authorUpperCase',
};

type ResponseValueObjectType = {
  dsc: string;
  dscEn: string;
  fioShort: string;
  fioShortEn: string;
  uref: string;
  webSite: string;
};

type ResponseFieldsType = {
  n: string;
  v: string | number | ResponseValueObjectType;
};

export type SearchResponseType = {
  data: string;
  fields: ResponseFieldsType[];
};

/**
 * Map backend nested data structures to a flat structure with human-readable attribute names
 * @param arr
 * @returns {*}
 */
export const responseToCorrectArray = (arr: SearchResponseType[]): KeyValue<string | unknown>[] => {
  return arr.map((item) => {
    const newObj: KeyValue<string | unknown> = {
      data: item.data,
      ...item.fields.reduce((out: KeyValue<string | unknown>, field) => {
        const isOnce: ResponseFieldsType['v'] = mapOnce[field.n as keyof ResponseFieldsType['v']];
        const nameMap = isOnce ? mapOnce : mapMultiple;
        const normalTitle: ResponseFieldsType['v'] = nameMap[field.n as keyof ResponseFieldsType['v']];
        if (isOnce) {
          //   out[normalTitle as unknown as string] = '123';
          out[normalTitle as unknown as string] = field.v;
        } else if (!out[normalTitle]) {
          out[normalTitle] = item.fields.filter((f) => f.n === field.n).map((f) => f.v);
        }
        return out;
      }, {}),
    };

    if (newObj.fileName || newObj.fileNameUpperCase) {
      newObj.file = {
        contentType: newObj.fileMimeType,
        fileSize: newObj.fileSize,
        fileType: 100,
        filename: newObj.fileNameUpperCase ?? 'download',
        id: newObj.id,
        path: `/publication/${newObj.id}/content`,
      };
    }
    return newObj;
  });
};

type SectionConfig = {
  fields: {
    author?: {
      labelKey: string;
      placeholderKey: string;
      name: string;
    };
    keywords?: {
      labelKey: string;
      placeholderKey: string;
      name: string;
    };
    city?: {
      labelKey: string;
      placeholderKey: string;
      name: string;
    };
    gender?: {
      labelKey: string;
      placeholderKey: string;
      name: string;
    };
    workplace?: {
      labelKey: string;
      placeholderKey: string;
      name: string;
    };
    topics?: {
      labelKey: string;
      placeholderKey: string;
      name: string;
    };
  };
  dateFilter?: {
    titleKey: string;
    type: 'date' | 'year';
    startDateName: 'startPostDate' | 'startYear';
    endDateName: 'endPostDate' | 'endYear';
    format: 'yyyy' | 'yyyy-MM-dd';
  };
};

export const sectionsConfig: Record<SectionEnum, SectionConfig> = {
  [SectionEnum.all]: {
    dateFilter: {
      endDateName: 'endYear',
      format: 'yyyy',
      startDateName: 'startYear',
      titleKey: 'search.by.date',
      type: 'year',
    },
    fields: {
      author: {
        labelKey: 'search.by.author',
        name: 'author',
        placeholderKey: 'search.filter.author.placeholder',
      },
      keywords: {
        labelKey: 'keywords',
        name: 'keywords',
        placeholderKey: 'search.enter.keywords',
      },
    },
  },
  [SectionEnum.people]: {
    fields: {
      city: {
        labelKey: 'search.filter.city',
        name: 'city',
        placeholderKey: 'search.filter.city.placeholder',
      },
      gender: {
        labelKey: 'search.filter.gender',
        name: 'gender',
        placeholderKey: 'search.filter.gender.placeholder',
      },
      workplace: {
        labelKey: 'search.filter.workplace',
        name: 'workplace',
        placeholderKey: 'search.filter.workplace.placeholder',
      },
    },
  },
  [SectionEnum.post]: {
    dateFilter: {
      endDateName: 'endPostDate',
      format: 'yyyy-MM-dd',
      startDateName: 'startPostDate',
      titleKey: 'search.by.post.date',
      type: 'date',
    },
    fields: {
      author: {
        labelKey: 'post.author',
        name: 'author',
        placeholderKey: 'search.filter.author.placeholder',
      },
      // topics: {
      //   labelKey: 'search.topics',
      //   name: 'topics',
      //   placeholderKey: 'search.post.topics.placeholder',
      // },
    },
  },
  [SectionEnum.publication]: {
    dateFilter: {
      endDateName: 'endYear',
      format: 'yyyy',
      startDateName: 'startYear',
      titleKey: 'search.by.publication.date',
      type: 'year',
    },
    fields: {
      author: {
        labelKey: 'search.by.publication.author',
        name: 'author',
        placeholderKey: 'search.filter.author.placeholder',
      },
      keywords: {
        labelKey: 'keywords',
        name: 'keywords',
        placeholderKey: 'search.enter.keywords',
      },
    },
  },
};

type SearchSection = {
  title: string;
  value: SectionEnum;
};

export const getSections = (t: TFunction): SearchSection[] => [
  { title: t(`search.filter.section.${SectionEnum.all}`), value: SectionEnum.all },
  { title: t(`search.filter.section.${SectionEnum.people}`), value: SectionEnum.people },
  { title: t(`search.filter.section.${SectionEnum.post}`), value: SectionEnum.post },
  { title: t(`search.filter.section.${SectionEnum.publication}`), value: SectionEnum.publication },
];
